import type React from "react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Users,
  Palette,
  Camera,
  Video,
  Share2,
  Trophy,
  Heart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const onboardingSteps = [
  {
    title: "Welcome to DreamWorld!",
    description:
      "Step into your creative hub inside the Pi Ecosystem. Generate, edit, and share AI-powered images and videos, all while earning rewards.",
    image: "https://i.imgur.com/2xHSjvB.jpeg",
    icon: Sparkles,
    color: "from-purple-600 to-pink-600",
    features: ["AI Studio", "Creative Rewards", "Social Community"],
  },
  {
    title: "Explore Effects & Styles",
    description:
      "Start instantly on the Home Page. Choose from powerful effects and dynamic video styles to bring your ideas to life.",
    image:
      "https://images.unsplash.com/photo-1756992293716-b843700b5ab0?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwzfHx8ZW58MHx8fHx8",
    icon: Palette,
    color: "from-blue-600 to-cyan-600",
    features: ["Photo Effects", "Video Styles", "Instant Previews"],
  },
  {
    title: "AI Studio: Create Anything",
    description:
      "Transform words into unique images, retouch your photos, or generate videos and animations — all in one place.",
    image:
      "https://plus.unsplash.com/premium_photo-1661954400491-41d29414d354?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8U3R1ZGlvfGVufDB8fDB8fHww",
    icon: Camera,
    color: "from-green-600 to-teal-600",
    features: ["Text-to-Image", "Smart Editing", "AI Video Creation"],
  },
  {
    title: "Your Personal Library",
    description:
      "Every creation is saved automatically. Organize into albums, keep them private, or prepare for publishing.",
    image:
      "https://plus.unsplash.com/premium_photo-1677187301535-b46cec7b2cc8?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8RWJvb2t8ZW58MHx8MHx8fDA%3D",
    icon: Video,
    color: "from-orange-600 to-red-600",
    features: ["Private Storage", "Albums", "Easy Access"],
  },
  {
    title: "Discover & Share",
    description:
      "Publish your work to the Discover page, get inspired by others, and engage with the global community.",
    image:
      "https://plus.unsplash.com/premium_photo-1668708034279-ab8fa3a9e19b?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8UHVycGxlfGVufDB8fDB8fHww",
    icon: Users,
    color: "from-indigo-600 to-purple-600",
    features: ["Trending Feed", "Likes & Comments", "Follow Creators"],
  },
  {
    title: "Join Events & Challenges",
    description:
      "Take part in themed contests, showcase your talent, and compete for rewards in Flux and Pi.",
    image:
      "https://plus.unsplash.com/premium_photo-1673435845886-5c9a3da80690?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fHJlZHxlbnwwfHwwfHx8MA%3D%3D",
    icon: Share2,
    color: "from-pink-600 to-rose-600",
    features: ["Weekly Challenges", "Prizes", "Event Archive"],
  },
  {
    title: "Grow & Get Rewarded",
    description:
      "Level up your profile with badges, achievements, and tips from fans. Earn recognition and rewards for your creativity.",
    image:
      "https://images.unsplash.com/photo-1548504773-429e84f586d2?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fG9yYW5nZXxlbnwwfHwwfHx8MA%3D%3D",
    icon: Trophy,
    color: "from-yellow-600 to-orange-600",
    features: ["Badges", "Tips in Pi", "Leaderboards"],
  },
  {
    title: "Ready to Begin?",
    description:
      "Your journey into creativity starts here. Enter DreamWorld and make something unforgettable today.",
    image:
      "https://plus.unsplash.com/premium_photo-1661909976446-3d686365b6f2?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTJ8fG5lb24lMjBnbG93fGVufDB8fDB8fHww",
    icon: Heart,
    color: "from-purple-600 to-pink-600",
    features: ["Seamless Onboarding", "24/7 Access", "Infinite Creativity"],
  },
];

const OnboardingPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setDirection(1);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };
  const navigate = useNavigate();

  const handleFinish = () => {
    navigate("/home");
  };

  const handleSkip = () => {
    navigate("/home");
  };

  const goToStep = (stepIndex: number) => {
    setDirection(stepIndex > currentStep ? 1 : -1);
    setCurrentStep(stepIndex);
  };

  const variants = {
    enter: (direction: number) => ({
      x:
        direction > 0
          ? typeof window !== "undefined" && window.innerWidth < 768
            ? 300
            : 1000
          : typeof window !== "undefined" && window.innerWidth < 768
            ? -300
            : -1000,
      opacity: 0,
      scale: typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 0.8,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x:
        direction < 0
          ? typeof window !== "undefined" && window.innerWidth < 768
            ? 300
            : 1000
          : typeof window !== "undefined" && window.innerWidth < 768
            ? -300
            : -1000,
      opacity: 0,
      scale: typeof window !== "undefined" && window.innerWidth < 768 ? 1 : 0.8,
    }),
  };

  const currentStepData = onboardingSteps[currentStep];
  const IconComponent = currentStepData.icon;

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden relative flex flex-col">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            rotate: 360,
          }}
          transition={{
            duration: 50,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
          className="absolute -top-40 -right-40 w-40 md:w-80 h-40 md:h-80 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-xl md:blur-3xl"
        />
        <motion.div
          animate={{
            rotate: -360,
          }}
          transition={{
            duration: 40,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
          className="absolute -bottom-40 -left-40 w-40 md:w-80 h-40 md:h-80 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-xl md:blur-3xl"
        />
      </div>

      {/* Skip button */}
      <button
        onClick={() => handleSkip()}
        className="absolute top-6 right-6 px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
        style={{ zIndex: 9999 }}
      >
        Skip the Tutorial
      </button>

      {/* Main container with proper flexbox layout */}
      <div className="relative z-10 flex flex-col h-full p-4">
        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl mx-auto mb-6 flex-shrink-0"
        >
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm text-gray-300">
              Step {currentStep + 1} of {onboardingSteps.length}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <motion.div
              className={`h-2 rounded-full bg-gradient-to-r ${currentStepData.color}`}
              initial={{ width: 0 }}
              animate={{
                width: `${((currentStep + 1) / onboardingSteps.length) * 100}%`,
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </motion.div>

        {/* Main content area - takes remaining space */}
        <div className="flex-1 flex items-center justify-center w-full max-w-4xl mx-auto">
          <div className="w-full relative">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentStep}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: {
                    type: "spring",
                    stiffness:
                      typeof window !== "undefined" && window.innerWidth < 768
                        ? 200
                        : 300,
                    damping:
                      typeof window !== "undefined" && window.innerWidth < 768
                        ? 25
                        : 30,
                  },
                  opacity: {
                    duration:
                      typeof window !== "undefined" && window.innerWidth < 768
                        ? 0.2
                        : 0.3,
                  },
                  scale: {
                    duration:
                      typeof window !== "undefined" && window.innerWidth < 768
                        ? 0.2
                        : 0.3,
                  },
                }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 md:p-8 shadow-2xl border border-white/20"
              >
                <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center">
                  {/* Content */}
                  <div className="space-y-4 md:space-y-6">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.2,
                        type: "spring",
                        stiffness: 200,
                      }}
                      className={`w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-gradient-to-r ${currentStepData.color} flex items-center justify-center`}
                    >
                      <IconComponent
                        size={24}
                        className="text-white md:w-8 md:h-8"
                      />
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
                    >
                      {currentStepData.title}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="text-base md:text-lg text-gray-300 leading-relaxed"
                    >
                      {currentStepData.description}
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="space-y-2 md:space-y-3"
                    >
                      {currentStepData.features.map((feature, index) => (
                        <motion.div
                          key={feature}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6 + index * 0.1 }}
                          className="flex items-center gap-3"
                        >
                          <div
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${currentStepData.color}`}
                          />
                          <span className="text-sm md:text-base text-gray-200">
                            {feature}
                          </span>
                        </motion.div>
                      ))}
                    </motion.div>
                  </div>

                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="relative"
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-r ${currentStepData.color} rounded-2xl blur-2xl opacity-30`}
                    />
                    <img
                      src={
                        currentStepData.image ||
                        "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                      }
                      alt={currentStepData.title}
                      className="relative z-10 w-full h-48 md:h-64 object-cover rounded-2xl shadow-2xl"
                    />
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation - always visible at bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex-shrink-0 flex items-center justify-between w-full max-w-4xl mx-auto mt-6 md:mt-8"
        >
          <button
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-white/20"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {currentStep === onboardingSteps.length - 1 ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleFinish}
              className={`px-6 md:px-8 py-2 md:py-3 bg-gradient-to-r ${currentStepData.color} rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200`}
            >
              <span className="hidden sm:inline">Start Creating!</span>
              <span className="sm:hidden">Start!</span>
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className={`flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r ${currentStepData.color} rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200`}
            >
              <span className="hidden sm:inline">Next</span>
              <ArrowRight size={20} />
            </motion.button>
          )}
        </motion.div>

        {/* Step indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="hidden md:flex justify-center mt-4 gap-2 flex-shrink-0"
        >
          {onboardingSteps.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToStep(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                currentStep === index
                  ? `bg-gradient-to-r ${currentStepData.color} shadow-lg`
                  : "bg-gray-600 hover:bg-gray-500"
              }`}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingPage;
